import { useUserServiceGetApiUserMe, useUserServiceGetApiUserDomain } from '../api/generated/queries/queries';
import { CurrentUserInfoDTO } from '../api/generated/requests/types.gen';

/**
 * Hook do pobierania informacji o aktualnie zalogowanym użytkowniku
 * Używa React Query do cache'owania danych
 */
export const useCurrentUser = () => {
  return useUserServiceGetApiUserMe(undefined, {
    staleTime: 10 * 60 * 1000, // 10 minut - dane użytkownika rzadko się zmieniają
    gcTime: 30 * 60 * 1000, // 30 minut w cache (gcTime is the new name for cacheTime)
  });
};

/**
 * Hook do pobierania wszystkich użytkowników w domenie
 * Używa React Query do cache'owania danych
 */
export const useDomainUsers = () => {
  return useUserServiceGetApiUserDomain(undefined, {
    staleTime: 10 * 60 * 1000, // 10 minut - lista użytkowników może się zmieniać częściej
    gcTime: 30 * 60 * 1000, // 30 minut w cache (gcTime is the new name for cacheTime)
  });
};

/**
 * Hook kombinowany do pobierania zarówno aktualnego użytkownika jak i wszystkich użytkowników domeny
 * Optymalizuje pobieranie danych gdy potrzebujemy obu zestawów
 */
export const useUserData = () => {
  const currentUserQuery = useCurrentUser();
  const domainUsersQuery = useDomainUsers();

  return {
    currentUser: currentUserQuery.data,
    domainUsers: domainUsersQuery.data || [],
    isLoadingCurrentUser: currentUserQuery.isLoading,
    isLoadingDomainUsers: domainUsersQuery.isLoading,
    isLoading: currentUserQuery.isLoading || domainUsersQuery.isLoading,
    error: currentUserQuery.error || domainUsersQuery.error,
    refetchCurrentUser: currentUserQuery.refetch,
    refetchDomainUsers: domainUsersQuery.refetch,
    refetchAll: () => {
      currentUserQuery.refetch();
      domainUsersQuery.refetch();
    }
  };
};

/**
 * Funkcja pomocnicza do uzyskania pełnego imienia i nazwiska użytkownika
 */
export const getUserFullName = (user: CurrentUserInfoDTO | null | undefined): string => {
  if (!user) return 'Nieprzypisane';

  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login || 'Nieznany użytkownik';
};
